import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { 
  AdvancedSearchRequestDTO, 
  SearchScope, 
  SearchOperator, 
  SortOption, 
  SortDirection, 
  ContentType 
} from '../../../models/search.model';

export interface SearchField {
  text: string;
  scope: string;
  operator: string;
}

@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.css'],
    inputs: ['isEmbedded', 'parentScope'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdvancedSearchComponent implements OnInit {
  isEmbedded = false;
  parentScope: number = 0; // Default to All

  searchFields: SearchField[] = [
    { text: '', scope: 'all', operator: 'and' }
  ];

  // Filters
  selectedYearRange: { from: string | null, to: string | null } = { from: null, to: null };
  selectedLanguages: string[] = [];
  selectedJournals: string[] = [];
  selectedBooks: string[] = [];

  // Options
  searchScopes = [
    { value: 'all', label: 'SEARCH_SCOPE_ALL_FIELDS' },
    { value: 'title', label: 'SEARCH_SCOPE_TITLE' },
    { value: 'abstract', label: 'SEARCH_SCOPE_ABSTRACT' },
    { value: 'keywords', label: 'SEARCH_SCOPE_KEYWORDS' },
    { value: 'author', label: 'SEARCH_SCOPE_AUTHOR' },
    { value: 'doi', label: 'SEARCH_SCOPE_DOI' }
  ];

  searchOperators = [
    { value: 'and', label: 'SEARCH_OPERATOR_AND' },
    { value: 'or', label: 'SEARCH_OPERATOR_OR' },
    { value: 'not', label: 'SEARCH_OPERATOR_NOT' }
  ];

  languages = [
    { value: 'tr', label: 'LANGUAGE_TR' },
    { value: 'en', label: 'LANGUAGE_EN' },
    { value: 'de', label: 'LANGUAGE_DE' },
    { value: 'fr', label: 'LANGUAGE_FR' }
  ];

  sortOptions = [
    { value: 'publishDate', label: 'SORT_PUBLISH_DATE' },
    { value: 'title', label: 'SORT_TITLE' }
  ];

  selectedSortOption = 'publishDate';
  selectedSortDirection = 'desc';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  isLanguageSelected(lang: string): boolean {
    return this.selectedLanguages.includes(lang);
  }

  ngOnInit(): void {
    // URL'deki parametreleri oku ve state'i doldur
    this.route.queryParams.subscribe(params => {
      const q = params['q'];
      if (q) {
        try {
          const request: AdvancedSearchRequestDTO = JSON.parse(q);
          this.populateFromRequest(request);
        } catch (e) {
          console.error('Advanced Search parse error', e);
        }
      }
    });
  }

  private populateFromRequest(request: AdvancedSearchRequestDTO): void {
    // Search Fields population
    if (request.SearchFields && request.SearchFields.length > 0) {
      this.searchFields = request.SearchFields.map(f => ({
        text: f.Text,
        scope: this.reverseMapScope(f.Scope),
        operator: this.reverseMapOperator(f.Operator)
      }));
    } else if (request.SearchTerm) {
      this.searchFields = [{
        text: request.SearchTerm,
        scope: this.reverseMapScope(request.Scope || SearchScope.All),
        operator: 'and'
      }];
    }



    // Year Range
    this.selectedYearRange = {
      from: request.YearFrom ? request.YearFrom.toString() : null,
      to: request.YearTo ? request.YearTo.toString() : null
    };

    // Filters (Journals, Books, Languages)
    if (request.LanguageIDs) {
      this.selectedLanguages = request.LanguageIDs.map(id => this.reverseMapLanguage(id));
    }
    if (request.JournalIDs) {
      this.selectedJournals = request.JournalIDs.map(id => id.toString());
    }
    if (request.BookIDs) {
      this.selectedBooks = request.BookIDs.map(id => id.toString());
    }

    // Sort Options
    if (request.SortBy !== undefined) {
      this.selectedSortOption = this.reverseMapSortOption(request.SortBy);
    }
    if (request.SortDirection !== undefined) {
      this.selectedSortDirection = request.SortDirection === SortDirection.ASC ? 'asc' : 'desc';
    }
  }

  private reverseMapScope(scope: SearchScope): string {
    const map: { [key: number]: string } = {
      [SearchScope.All]: 'all',
      [SearchScope.Title]: 'title',
      [SearchScope.Abstract]: 'abstract',
      [SearchScope.Keywords]: 'keywords',
      [SearchScope.Author]: 'author',
      [SearchScope.DOI]: 'doi'
    };
    return map[scope] || 'all';
  }

  private reverseMapOperator(op: SearchOperator): string {
    const map: { [key: number]: string } = {
      [SearchOperator.AND]: 'and',
      [SearchOperator.OR]: 'or',
      [SearchOperator.NOT]: 'not'
    };
    return map[op] || 'and';
  }

  private reverseMapContentType(type: ContentType): string {
    const map: { [key: number]: string } = {
      [ContentType.Article]: 'article',
      [ContentType.Book]: 'book',
      [ContentType.Journal]: 'journal',
      [ContentType.Author]: 'author'
    };
    return map[type] || 'article';
  }

  private reverseMapSortOption(option: SortOption): string {
    const map: { [key: number]: string } = {
      [SortOption.PublishDate]: 'publishDate',
      [SortOption.Title]: 'title',
      [SortOption.Relevance]: 'relevance'
    };
    return map[option] || 'publishDate';
  }

  private mapLanguage(lang: string): number {
    const map: { [key: string]: number } = {
      'tr': 1,
      'en': 2,
      'de': 3,
      'fr': 4
    };
    return map[lang] || 1;
  }

  private reverseMapLanguage(id: number): string {
    const map: { [key: number]: string } = {
      1: 'tr',
      2: 'en',
      3: 'de',
      4: 'fr'
    };
    return map[id] || 'tr';
  }

  addSearchField(): void {
    this.searchFields.push({ text: '', scope: 'all', operator: 'and' });
  }

  removeSearchField(index: number): void {
    if (this.searchFields.length > 1) {
      this.searchFields.splice(index, 1);
    }
  }

  toggleLanguage(lang: string): void {
    const index = this.selectedLanguages.indexOf(lang);
    if (index > -1) {
      this.selectedLanguages.splice(index, 1);
    } else {
      this.selectedLanguages.push(lang);
    }
  }

  search(): void {
    // Prepare search request matching backend AdvancedSearchRequestDTO
    const searchRequest: AdvancedSearchRequestDTO = {
      SearchTerm: '',
      Scope: SearchScope.All,
      
      // Sayfalama
      Page: 1,
      Take: 20,
      
      // Sıralama
      SortBy: this.mapSortOption(this.selectedSortOption),
      SortDirection: this.selectedSortDirection === 'desc' ? SortDirection.DESC : SortDirection.ASC,
      
      // Operatör
      Operator: SearchOperator.AND, // Varsayılan
      
      // İçerik Tipleri
      ContentTypes: this.isEmbedded ? [this.parentScope] : [ContentType.All],
        
      // Filtreler
      JournalIDs: this.selectedJournals.map(id => Number(id)),
      BookIDs: this.selectedBooks.map(id => Number(id)),
      LanguageIDs: this.selectedLanguages.map(lang => this.mapLanguage(lang)),
      
      // Tarih Filtreleri
      YearFrom: this.selectedYearRange.from ? Number(this.selectedYearRange.from) : null,
      YearTo: this.selectedYearRange.to ? Number(this.selectedYearRange.to) : null
    };

    // Çoklu alan araması (IEEE tarzı) mapping
    searchRequest.SearchFields = [];
    this.searchFields.forEach(field => {
      if (!field.text.trim()) return;

      searchRequest.SearchFields?.push({
        Text: field.text.trim(),
        Scope: this.mapScope(field.scope),
        Operator: this.mapOperator(field.operator)
      });

      // Geriye dönük uyumluluk için (tek alanlı arama yapan yerler için)
      const text = field.text.trim();
      switch (field.scope) {
        case 'all':
          searchRequest.SearchTerm = text;
          searchRequest.Scope = SearchScope.All;
          break;
        case 'title':
          searchRequest.TitleSearch = text;
          break;
        case 'abstract':
          searchRequest.AbstractSearch = text;
          break;
        case 'author':
          searchRequest.AuthorSearch = text;
          break;
        case 'doi':
          searchRequest.DOISearch = text;
          break;
      }
    });

    // Default operator (usually AND)
    searchRequest.Operator = this.searchFields.length > 0 
      ? this.mapOperator(this.searchFields[0].operator) 
      : SearchOperator.AND;

    // Navigate to search results page with query params
    this.router.navigate(['/search/results'], {
      queryParams: { q: JSON.stringify(searchRequest) }
    });
  }

  resetSearch(): void {
    this.searchFields = [{ text: '', scope: 'all', operator: 'and' }];
    this.selectedYearRange = { from: null, to: null };
    this.selectedYearRange = { from: null, to: null };
    this.selectedLanguages = [];
    this.selectedJournals = [];
    this.selectedBooks = [];
    this.selectedSortOption = 'publishDate';
    this.selectedSortDirection = 'desc';
  }

  private mapScope(scope: string): SearchScope {
    const scopeMap: { [key: string]: SearchScope } = {
      'all': SearchScope.All,
      'title': SearchScope.Title,
      'abstract': SearchScope.Abstract,
      'keywords': SearchScope.Keywords,
      'author': SearchScope.Author,
      'doi': SearchScope.DOI
    };
    return scopeMap[scope] || SearchScope.All;
  }

  private mapOperator(operator: string): SearchOperator {
    const operatorMap: { [key: string]: SearchOperator } = {
      'and': SearchOperator.AND,
      'or': SearchOperator.OR,
      'not': SearchOperator.NOT
    };
    return operatorMap[operator] || SearchOperator.AND;
  }

  private mapContentType(type: string): ContentType {
    const typeMap: { [key: string]: ContentType } = {
      'article': ContentType.Article,
      'book': ContentType.Book,
      'journal': ContentType.Journal,
      'author': ContentType.Author
    };
    return typeMap[type] || ContentType.Article;
  }

  private mapSortOption(option: string): SortOption {
    const sortMap: { [key: string]: SortOption } = {
      'relevance': SortOption.Relevance,
      'publishDate': SortOption.PublishDate,
      'title': SortOption.Title
    };
    return sortMap[option] || SortOption.Relevance;
  }
}
